import React from 'react';

import {AudioPlayer} from './Audio/AudioPlayer';
import {ContentView} from './Content/ContentView';
import {CustomWeaveTypePayload} from './customWeaveType.types';
import {CustomWeaveTypeProjectContext} from './CustomWeaveTypeProjectContext';
import {DateTimeView} from './datetime.datetime/DateTimeView';
import {MarkdownView} from './Markdown/MarkdownView';
import {VideoPlayer} from './moviepy.video.VideoClip/VideoPlayer';
import {PILImageImage} from './PIL.Image.Image/PILImageImage';

type CustomWeaveTypeDispatcherProps = {
  data: CustomWeaveTypePayload;
  // Entity and Project can be optionally provided as props, but if they are not
  // provided, they must be provided in context. Failure to provide them will
  // result in a console warning and a fallback to a default component.
  //
  // This pattern is used because in many cases we are rendering data from
  // hierarchical data structures, and we want to avoid passing entity and project
  // down through the tree.
  entity?: string;
  project?: string;
};

const customWeaveTypeRegistry: {
  [typeId: string]: {
    component: React.FC<{
      entity: string;
      project: string;
      mode?: string;
      data: any; // I wish this could be typed more specifically
    }>;
    preferredRowHeight?: number;
    // Renders as a visual thumbnail (image / video / the media Content wrapper).
    // Consumers use this to give media taller rows + wider columns (see the Eval
    // Tables panel).
    showThumbnail?: boolean;
  };
} = {
  'PIL.Image.Image': {
    component: PILImageImage,
    preferredRowHeight: 350,
    showThumbnail: true,
  },
  'PIL.JpegImagePlugin.JpegImageFile': {
    component: PILImageImage,
    preferredRowHeight: 350,
    showThumbnail: true,
  },
  'PIL.PngImagePlugin.PngImageFile': {
    component: PILImageImage,
    preferredRowHeight: 350,
    showThumbnail: true,
  },
  'wave.Wave_read': {
    component: AudioPlayer,
  },
  'weave.type_handlers.Audio.audio.Audio': {
    component: AudioPlayer,
  },
  'datetime.datetime': {
    component: DateTimeView,
  },
  'rich.markdown.Markdown': {
    component: MarkdownView,
    preferredRowHeight: 350,
  },
  'weave.type_handlers.Markdown.markdown.Markdown': {
    component: MarkdownView,
    preferredRowHeight: 350,
  },
  'moviepy.video.VideoClip.VideoClip': {
    component: VideoPlayer,
    showThumbnail: true,
  },
  'weave.type_wrappers.Content.content.Content': {
    component: ContentView,
    showThumbnail: true,
  },
};

export const getCustomWeaveTypePreferredRowHeight = (
  typeId: string
): number | undefined => {
  return customWeaveTypeRegistry[typeId]?.preferredRowHeight;
};

export const customWeaveTypeShowsThumbnail = (typeId: string): boolean => {
  return customWeaveTypeRegistry[typeId]?.showThumbnail ?? false;
};

/**
 * This is the primary entry-point for dispatching custom weave types.
 */
export const CustomWeaveTypeDispatcher: React.FC<
  CustomWeaveTypeDispatcherProps
> = ({data, entity, project}) => {
  const projectContext = React.useContext(CustomWeaveTypeProjectContext);
  const typeId = data.weave_type.type;
  const comp = customWeaveTypeRegistry[typeId]?.component;
  const defaultReturn = <span>Custom Weave Type: {typeId}</span>;

  if (comp) {
    const applicableEntity = entity || projectContext?.entity;
    const applicableProject = project || projectContext?.project;
    if (applicableEntity == null || applicableProject == null) {
      console.warn(
        'CustomWeaveTypeDispatch: entity and project must be provided in context or as props'
      );
      return defaultReturn;
    }
    return React.createElement(comp, {
      entity: applicableEntity,
      project: applicableProject,
      mode: projectContext?.mode,
      data,
    });
  }

  return defaultReturn;
};
